import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

// Helper function to format price with two decimals
const formatPrice = (priceString) => {
  // Extract number from string like "CHF 9500.0" or "CHF 9500"
  const match = priceString.match(/[\d,.]+/);
  if (!match) return priceString;
  
  const number = parseFloat(match[0].replace(',', ''));
  const currency = priceString.includes('CHF') ? 'CHF' : '€';
  
  return `${currency} ${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatueProductCard = ({ product, onExpand, isExpanded, onClose, onPreviousProduct, onNextProduct, hasMultipleProducts }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedColor, setSelectedColor] = useState(''); // Will be set from product data
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Use color variants from product data if available
  const hasColorVariantsNew = product.colorVariants && Object.keys(product.colorVariants).length > 0;
  const hasColorVariantsOld = product.hasColorVariants && product.colorOptions && product.colorOptions.length > 0;
  const hasColorVariants = hasColorVariantsNew || hasColorVariantsOld;
  
  const colorOptions = hasColorVariantsNew 
    ? Object.keys(product.colorVariants).map(color => ({ 
        name: color, 
        priceModifier: 0,
        sold: product.colorVariants[color]?.sold || false
      }))
    : hasColorVariantsOld
      ? product.colorOptions.map(color => ({ name: color, priceModifier: 0, sold: false }))
      : [{ name: 'Standard', priceModifier: 0, sold: false }];

  // Set initial color when component mounts or product changes - default to first NON-SOLD option
  useEffect(() => {
    if (colorOptions.length > 0 && !selectedColor) {
      // Find first non-sold color option
      const availableColor = colorOptions.find(opt => !opt.sold);
      const defaultColor = availableColor ? availableColor.name : colorOptions[0].name;
      setSelectedColor(defaultColor);
      
      // Set initial image based on color variant (new structure)
      if (product.colorVariants && product.colorVariants[defaultColor]) {
        const imageIndex = product.colorVariants[defaultColor].imageIndex;
        if (imageIndex !== undefined) {
          setCurrentImageIndex(imageIndex);
        }
      }
      // Or use old colorOptions structure (based on array position)
      else if (product.colorOptions && product.colorOptions.length > 0) {
        const colorIndex = product.colorOptions.findIndex(color => color === defaultColor);
        if (colorIndex !== -1 && colorIndex < product.images.length) {
          setCurrentImageIndex(colorIndex);
        }
      }
    }
  }, [product.id, colorOptions.length]);

  // Handle color change and update image
  const handleColorChange = (newColor) => {
    setSelectedColor(newColor);
    
    // If product has colorVariants with imageIndex, use that
    if (product.colorVariants && product.colorVariants[newColor]) {
      const imageIndex = product.colorVariants[newColor].imageIndex;
      if (imageIndex !== undefined && imageIndex < product.images.length) {
        setCurrentImageIndex(imageIndex);
      }
    }
    // Fallback to old method for products with colorOptions
    else if (product.colorOptions) {
      const colorIndex = product.colorOptions.findIndex(color => color === newColor);
      if (colorIndex !== -1 && colorIndex < product.images.length) {
        setCurrentImageIndex(colorIndex);
      }
    }
  };

  // Get current price with color modifier or from colorVariants
  const getCurrentPrice = () => {
    let priceValue;
    
    // Check if colorVariants has specific pricing
    if (product.colorVariants && selectedColor && product.colorVariants[selectedColor]) {
      const variantPrice = product.colorVariants[selectedColor].price;
      // Extract numeric value from formatted price
      priceValue = parseFloat(variantPrice.replace(/[^0-9.-]+/g, ''));
    } else {
      // Fallback to old method
      const basePrice = parseFloat(product.price.replace(/[^0-9.-]+/g, ''));
      const colorOption = colorOptions.find(opt => opt.name === selectedColor);
      priceValue = basePrice + (colorOption ? colorOption.priceModifier : 0);
    }
    
    // Always format consistently as "CHF xxx.00"
    return `CHF ${priceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get current dimensions based on selected color
  const getCurrentDimensions = () => {
    if (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.dimensions) {
      return product.colorVariants[selectedColor].dimensions;
    }
    return {
      height: product.height,
      width: product.width,
      depth: product.depth
    };
  };

  // Helper function to check if the current displayed image is for a sold variant
  const isCurrentImageSold = () => {
    // If the entire product is sold, return true
    if (product.sold) return true;
    
    // Check if we have color variants and the current image corresponds to a sold variant
    if (product.colorVariants && Object.keys(product.colorVariants).length > 0) {
      // Find which color variant corresponds to the current image index
      const colorEntries = Object.entries(product.colorVariants);
      const currentVariant = colorEntries.find(([color, variant]) => 
        variant.imageIndex === currentImageIndex
      );
      
      // If we found a variant for this image and it's sold, return true
      if (currentVariant && currentVariant[1].sold) {
        return true;
      }
    }
    
    return false;
  };

  const handleAddToCart = (e) => {
    if (e) e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      images: product.images,
      category: 'Sculpture'
    });
    setAddedToCart(true);
    
    // Close modal after short delay
    setTimeout(() => {
      setAddedToCart(false);
      if (onClose) onClose();
    }, 1000);
  };

  // Navigate to next image when clicking on the image
  const handleImageClick = (e) => {
    e.stopPropagation();
    if (product.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % product.images.length
      );
    }
  };

  // Navigate to previous image
  const handlePreviousImage = (e) => {
    e.stopPropagation();
    if (product.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
      );
    }
  };

  // Navigate to next image
  const handleNextImage = (e) => {
    e.stopPropagation();
    if (product.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % product.images.length
      );
    }
  };

  // Auto-carousel effect when hovering over products with multiple images
  useEffect(() => {
    let interval;
    if (isHovered && product.images.length > 1 && !isExpanded) {
      interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % product.images.length
        );
      }, 1500);
    } else if (!isHovered) {
      setCurrentImageIndex(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovered, product.images.length, isExpanded]);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product.id]);

  // Grid card view
  if (!isExpanded) {
    return (
      <motion.div
        layout
        layoutId={`card-${product.id}`}
        onClick={() => navigate(`/product/${product.id}`)}
        className="cursor-pointer group relative overflow-hidden rounded-lg transition-all duration-500 hover:shadow-2xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        {/* Card Container */}
        <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 aspect-square overflow-hidden">
          {/* Product Image */}
          <motion.img
            layoutId={`image-${product.id}`}
            src={product.images[currentImageIndex]}
            alt={product.name}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />

          {/* SOLD Tag Overlay */}
          {isCurrentImageSold() && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-black/40">
              <div className="relative">
                <div className="text-6xl font-black text-white transform -rotate-12 tracking-wider" style={{
                  textShadow: '0 0 20px rgba(0,0,0,0.8), 0 0 40px rgba(255,0,0,0.5), 3px 3px 0px rgba(0,0,0,0.3)',
                  WebkitTextStroke: '2px rgba(255,255,255,0.3)'
                }}>
                  SOLD
                </div>
                <div className="absolute inset-0 border-8 border-white/30 transform rotate-12"></div>
              </div>
            </div>
          )}

          {/* Multiple Images Indicator */}
          {product.images.length > 1 && (
            <div className="absolute top-4 right-4 flex gap-1 z-10">
              {product.images.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? 'w-6 bg-[hsl(45,100%,70%)]'
                      : 'w-1 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Golden Glow Border Effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(212,175,55,0.6)]" />
          </div>
        </div>

        {/* Product Info */}
        <motion.div layout className="p-6 bg-white">
          <h3 className="text-xl font-bold text-gray-900 group-hover:text-[hsl(45,100%,70%)] transition-colors duration-300">
            {product.name}
          </h3>
        </motion.div>
      </motion.div>
    );
  }

  // Expanded view
  return (
    <motion.div
      layout
      layoutId={`card-${product.id}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Previous Product Arrow (Left) */}
      {hasMultipleProducts && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreviousProduct();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-2xl flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 z-50"
          aria-label="Previous product"
        >
          <ChevronLeft className="w-8 h-8 text-gray-800" />
        </button>
      )}

      {/* Next Product Arrow (Right) */}
      {hasMultipleProducts && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNextProduct();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-2xl flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 z-50"
          aria-label="Next product"
        >
          <ChevronRight className="w-8 h-8 text-gray-800" />
        </button>
      )}

      <motion.div
        className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        layout
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <X className="w-6 h-6 text-gray-800" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-y-auto">
          {/* Left: Product Image/Video (60%) */}
          <div 
            className="lg:col-span-3 relative bg-gradient-to-br from-gray-100 to-gray-200 aspect-square cursor-pointer group/image"
            onClick={handleImageClick}
            title={product.images.length > 1 ? "Click to see next image" : ""}
          >
            {product.images[currentImageIndex]?.endsWith('.mp4') ? (
              <video
                src={product.images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-contain p-8"
                controls
                autoPlay
                loop
                muted
              />
            ) : (
              <motion.img
                key={currentImageIndex}
                src={product.images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-contain p-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            )}

            {/* SOLD Tag Overlay in Modal - show for entire product OR selected color variant sold */}
            {(product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold)) && (
              <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-black/40">
                <div className="bg-black bg-opacity-70 px-8 py-4 rounded-lg">
                  <span className="text-white text-4xl font-bold tracking-wider">SOLD OUT</span>
                </div>
              </div>
            )}

            {/* Left Arrow Button - Image Navigation (Golden & Smaller) */}
            {product.images.length > 1 && (
              <button
                onClick={handlePreviousImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[hsl(45,100%,70%)]/95 backdrop-blur-sm shadow-lg flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-all duration-300 hover:bg-[hsl(45,100%,60%)] hover:scale-110 z-10"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-5 h-5 text-black" />
              </button>
            )}

            {/* Right Arrow Button - Image Navigation (Golden & Smaller) */}
            {product.images.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[hsl(45,100%,70%)]/95 backdrop-blur-sm shadow-lg flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-all duration-300 hover:bg-[hsl(45,100%,60%)] hover:scale-110 z-10"
                aria-label="Next image"
              >
                <ChevronRight className="w-5 h-5 text-black" />
              </button>
            )}

            {/* Image Navigation Dots */}
            {product.images.length > 1 && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                {product.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentImageIndex
                        ? 'bg-[hsl(45,100%,70%)] w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: Specifications Panel (40%) */}
          <motion.div
            className="lg:col-span-2 p-8 lg:p-12 flex flex-col overflow-y-auto"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {/* Product Name */}
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              {product.name}
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Color Selector - Only show if product has color variants */}
            {hasColorVariants && (
              <div className="mb-8">
                <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
                  Select Color
                </label>
                <select
                  value={selectedColor}
                  onChange={(e) => {
                    handleColorChange(e.target.value);
                  }}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 font-medium focus:border-[hsl(45,100%,70%)] focus:outline-none transition-colors cursor-pointer hover:border-[hsl(45,100%,70%)]"
                >
                  {colorOptions.map((option) => (
                    <option 
                      key={option.name} 
                      value={option.name}
                      style={option.sold ? { backgroundColor: '#e5e7eb', color: '#6b7280' } : {}}
                    >
                      {option.sold ? `${option.name} - SOLD` : option.name}
                      {!option.sold && option.priceModifier > 0 ? ` (+CHF ${option.priceModifier})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Choose your preferred color variant
                </p>
              </div>
            )}

            {/* Dimensions Section */}
            <div className="mb-8">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                Dimensions
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-[hsl(45,100%,70%)] flex items-baseline justify-center gap-1">
                    <span>{getCurrentDimensions().height?.replace(' cm', '')}</span>
                    <span className="text-base">cm</span>
                  </div>
                  <div className="text-base font-semibold text-gray-600 mt-2">Height</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-[hsl(45,100%,70%)] flex items-baseline justify-center gap-1">
                    <span>{getCurrentDimensions().width?.replace(' cm', '')}</span>
                    <span className="text-base">cm</span>
                  </div>
                  <div className="text-base font-semibold text-gray-600 mt-2">Width</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-[hsl(45,100%,70%)] flex items-baseline justify-center gap-1">
                    <span>{getCurrentDimensions().depth?.replace(' cm', '')}</span>
                    <span className="text-base">cm</span>
                  </div>
                  <div className="text-base font-semibold text-gray-600 mt-2">Depth</div>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="mb-8 text-center">
              <div className="text-4xl font-bold text-[hsl(45,100%,70%)]">
                {getCurrentPrice()}
              </div>
            </div>

            {/* Add to Cart Button */}
            <button 
              onClick={handleAddToCart}
              disabled={product.sold || addedToCart || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold)}
              className={`w-full font-bold py-4 px-8 rounded-full transition-all duration-300 transform flex items-center justify-center gap-2 ${
                (product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold))
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : addedToCart 
                  ? 'bg-green-500 text-white' 
                  : 'bg-[hsl(45,100%,70%)] text-black hover:bg-[hsl(45,100%,60%)] hover:scale-105 hover:shadow-lg'
              }`}
            >
              {(product.sold || (product.colorVariants && selectedColor && product.colorVariants[selectedColor]?.sold)) ? (
                <>
                  SOLD OUT
                </>
              ) : addedToCart ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Added to Cart!
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </>
              )}
            </button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StatueProductCard;
